import React, { useEffect, useRef, useState } from 'react';
import { beginKoppelen, bevestigCode, maakHerstelcodes, stopKoppelen } from '../../services/tweestapsService';
import { formatteerGeheim, schoonCode, tweestapsFoutMelding } from '../../utils/tweestaps';
import TweestapsHerstelcodes from './TweestapsHerstelcodes';
import './Tweestaps.css';

/**
 * Authenticator-app koppelen: QR-code scannen, code bevestigen, herstelcodes bewaren.
 * Gebruikt op het scherm na inloggen (verplicht) en bij Instellingen (vrijwillig).
 */
function TweestapsKoppelen({ email, onKlaar, onAnnuleer = null }) {
  const [fase, setFase] = useState('laden'); // laden | scannen | codes | fout
  const [koppeling, setKoppeling] = useState(null); // { factorId, qrCode, geheim }
  const [code, setCode] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const [codes, setCodes] = useState([]);
  const [geenCodes, setGeenCodes] = useState(false);
  const [toonGeheim, setToonGeheim] = useState(false);
  const gestart = useRef(false);

  const start = async () => {
    setFase('laden');
    setFout('');
    try {
      setKoppeling(await beginKoppelen());
      setFase('scannen');
    } catch (e) {
      setFout(e?.code === 'insufficient_aal'
        ? 'Er is al een app gekoppeld. Log opnieuw in met de code uit die app.'
        : 'De QR-code kon niet worden gemaakt. Probeer het opnieuw.');
      setFase('fout');
    }
  };

  useEffect(() => {
    // Eén keer per scherm (StrictMode draait effecten in ontwikkeling twee keer).
    if (gestart.current) return;
    gestart.current = true;
    start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const annuleer = onAnnuleer && (async () => {
    if (fase === 'scannen') await stopKoppelen(koppeling?.factorId);
    onAnnuleer();
  });

  const bevestig = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !koppeling) return;
    setBezig(true);
    setFout('');
    try {
      await bevestigCode(koppeling.factorId, code);
    } catch (err) {
      setFout(tweestapsFoutMelding(err));
      setCode('');
      setBezig(false);
      return;
    }
    try {
      setCodes(await maakHerstelcodes());
    } catch {
      setGeenCodes(true);
    }
    setBezig(false);
    setFase('codes');
  };

  if (fase === 'laden') {
    return <p className="ts-uitleg">QR-code maken…</p>;
  }

  if (fase === 'fout') {
    return (
      <div>
        <div className="ts-fout" role="alert">{fout}</div>
        <button type="button" className="ts-knop" onClick={start}>Opnieuw proberen</button>
        {annuleer && <button type="button" className="ts-link ts-link-stil" onClick={annuleer}>Annuleren</button>}
      </div>
    );
  }

  if (fase === 'codes') {
    return (
      <div>
        <div className="ts-ok" role="status">De tweede stap staat aan. Bij inloggen vragen we voortaan de code uit je app.</div>
        {geenCodes || codes.length === 0 ? (
          <>
            <p className="ts-uitleg">
              Herstelcodes konden nu niet worden gemaakt. Maak ze later bij Instellingen, onder Tweestapsverificatie.
            </p>
            <button type="button" className="ts-knop" onClick={onKlaar}>Verder</button>
          </>
        ) : (
          <TweestapsHerstelcodes codes={codes} email={email} onKlaar={onKlaar} knopTekst="Verder" />
        )}
      </div>
    );
  }

  // Breed (Instellingen): links de QR-code, rechts de stappen en de code.
  // Smal (inlogscherm, telefoon): onder elkaar in deze volgorde (25-10-2026).
  return (
    <div className="ts-koppelen-houder">
      <form className="ts-koppelen" onSubmit={bevestig}>
        <ol className="ts-stappen ts-koppelen-stappen">
          <li>Open een authenticator-app op je telefoon, bijvoorbeeld Microsoft Authenticator of Google Authenticator.</li>
          <li>Scan de QR-code met die app.</li>
          <li>Vul de 6 cijfers in die de app toont.</li>
        </ol>
        <div className="ts-koppelen-qr">
          <div className="ts-qr">
            {koppeling?.qrCode && <img src={koppeling.qrCode} alt="QR-code om TestWisely aan je authenticator-app toe te voegen" />}
          </div>
          <div className="ts-geheim">
            {toonGeheim ? (
              <>Typ deze sleutel over in de app:<br /><code>{formatteerGeheim(koppeling?.geheim)}</code></>
            ) : (
              <button type="button" className="ts-link" style={{ margin: '0 auto' }} onClick={() => setToonGeheim(true)}>Lukt scannen niet?</button>
            )}
          </div>
        </div>
        <div className="ts-koppelen-invoer">
          {fout && <div className="ts-fout" role="alert">{fout}</div>}
          <div className="ts-veld">
            <label htmlFor="ts-koppel-code">Code uit de app</label>
            <input
              id="ts-koppel-code"
              className="ts-invoer ts-invoer-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(schoonCode(e.target.value))}
              disabled={bezig}
            />
          </div>
          <button type="submit" className="ts-knop" disabled={bezig || code.length !== 6}>
            {bezig ? 'Bezig…' : 'Bevestigen'}
          </button>
          {annuleer && <button type="button" className="ts-link ts-link-stil" onClick={annuleer} disabled={bezig}>Annuleren</button>}
        </div>
      </form>
    </div>
  );
}

export default TweestapsKoppelen;
